"use client";

import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useModalScrollLock } from "@/hooks/useModalScrollLock";
import PopupForm from "@/components/newPageComponent/CommonSection/PopupForm";
import { getFormOneDataFromLocal } from "@/lib/edcrypt";

const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF_ENQUIRY";

interface CairaNewProps {
  pageName: string;
}

const LOGO_URL =
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/CAIRA3.webp";
const DESKTOP_BANNER_URL =
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/CAIRA_Banner_PC.webp";
const MOBILE_BANNER_URL =
  "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/CAIRA_banner_mob.webp";

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0, marginTop: "2px" }}
  >
    <circle cx="10" cy="10" r="10" fill="#FFBA08" opacity="0.12" />
    <circle
      cx="10"
      cy="10"
      r="8"
      fill="none"
      stroke="#FFBA08"
      strokeWidth="1.5"
    />
    <path
      d="M6.5 10L8.8 12.3L13.5 7.5"
      stroke="#FFBA08"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CairaNew: React.FC<CairaNewProps> = ({ pageName }) => {
  const router = useRouter();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const handleFormModalOpen = () => {
    const { status: isFormOneFilled } =
      getFormOneDataFromLocal(LOCAL_STORE_NAME);
    if (isFormOneFilled) {
      router.push("/caira");
      return;
    }
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModalClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  return (
    <>
      <section
        className="relative w-full overflow-hidden bg-[#061a2f] bg-cover bg-center"
        style={{
          backgroundImage: `url("https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/cairabgIMG.webp")`,
        }}
      >
        {/* Dark overlay (replaces ::before pseudo-element) */}
        <div className="absolute inset-0 bg-black/30 z-0 pointer-events-none" />

        {/* Main container */}
        <div className="main_container relative z-10 flex flex-col items-center text-center gap-4 w-full pt-8 pb-0 md:flex-row md:items-center md:text-left md:gap-8 md:py-12">
          {/* Logo */}
          <div className="flex-none flex items-center justify-center">
            <img
              className="w-[100px] h-auto md:w-[170px]"
              src={LOGO_URL}
              alt="CAIRA Level 1 Badge"
              loading="lazy"
              decoding="async"
            />
          </div>

          {/* Text */}
          <div className="flex flex-col items-center gap-2 max-w-[387px] md:items-start md:gap-3">
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/CAIRALogo.webp"
                alt="CAIRA logo"
                loading="lazy"
                width={135}
                height={35}
              />
            </div>
            <h3 className="text-xs font-bold text-[#FFBA08] m-0 md:text-base">
              The Certified AI-Ready Accountant
            </h3>
            <ul className="flex flex-col gap-2 m-0 p-0 list-none">
              <li className="flex items-start gap-2 text-sm text-white">
                <CheckIcon />
                <span>Learn AI mindset, skillset &amp; toolset</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-white">
                <CheckIcon />
                <span>Build AI workflows &amp; agents</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-white">
                <CheckIcon />
                <span>Apply AI across audit, tax, and finance</span>
              </li>
            </ul>
            <p className="text-sm text-white font-medium border-[1px] border-[#FFBA08] rounded-[5px] px-[10px] py-[5px]">
              3 Levels | 90 Hours | NASBA-approved CPE
            </p>
            <button
              onClick={handleFormModalOpen}
              className="mt-4 py-3 px-6 text-sm font-semibold bg-white rounded-full cursor-pointer self-center hover:bg-white hover:text-[#060d1b] md:self-start"
            >
              Know more about CAIRA
            </button>
          </div>
        </div>

        {/* Banner (absolute on desktop so it can extend past the main_container, flush to the section's right edge) */}
        <div className="relative z-10 w-full flex items-end justify-center mt-2 md:absolute md:inset-y-0 md:right-0 md:z-[5] md:mt-0 md:w-auto md:items-stretch md:justify-end">
          <img
            className="hidden md:block md:h-full md:w-auto md:object-cover md:object-left-bottom"
            src={DESKTOP_BANNER_URL}
            alt="CAIRA AI Accountant Program"
            loading="lazy"
            decoding="async"
          />
          <img
            className="block w-full max-w-[420px] h-auto object-contain md:hidden"
            src={MOBILE_BANNER_URL}
            alt="CAIRA AI Accountant Program"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {isFormModalOpen && (
        <PopupForm handleClose={handleFormModalClose} pageName={pageName} />
      )}
    </>
  );
};

export default CairaNew;
